// Centralised fetch client for the Clip Builder card.
//
// Every request against a clip-builder segment goes through here so the CSRF
// header, JSON/FormData shaping, and endpoint URLs live in exactly one place
// instead of being copy-pasted across the (previously monolithic) Stimulus
// controllers. Pure of any Stimulus dependency — trivially unit-testable with
// a stubbed `fetch`.

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content
}

export default class ClipBuilderApi {
  constructor({
    uploadUrl,
    updateUrl,
    generateUrl,
    availableRendersUrl,
    selectRenderUrl,
    previewUrl,
    previewStatusUrl,
    segmentIndex,
  } = {}) {
    this.uploadUrl = uploadUrl
    this.updateUrl = updateUrl
    this.generateUrl = generateUrl
    this.availableRendersUrl = availableRendersUrl
    this.selectRenderUrl = selectRenderUrl
    this.previewUrl = previewUrl
    this.previewStatusUrl = previewStatusUrl
    this.segmentIndex = segmentIndex
  }

  // PATCH one or more segment fields. Accepts any field map (plain fields,
  // overlay arrays, or the "__remove__N" sentinel the overlay endpoints use).
  updateSegment(fields) {
    return fetch(this.updateUrl, {
      method: "PATCH",
      headers: this.jsonHeaders(),
      body: JSON.stringify({ segment_index: this.segmentIndex, ...fields }),
    })
  }

  // Upload an asset (image | audio | video | logo). Returns both the raw
  // Response and the parsed JSON body so callers can branch on status.
  async uploadAsset(file, assetType) {
    const formData = new FormData()
    formData.append("file", file)
    formData.append("segment_index", this.segmentIndex)
    formData.append("asset_type", assetType)

    const response = await fetch(this.uploadUrl, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken() },
      body: formData,
    })
    const data = await response.json().catch(() => ({}))
    return { response, data }
  }

  generateVideo(modelKey) {
    return fetch(this.generateUrl, {
      method: "POST",
      headers: this.jsonHeaders(),
      body: JSON.stringify({ segment_index: this.segmentIndex, model_key: modelKey }),
    })
  }

  fetchRenders() {
    return fetch(this.availableRendersUrl, { headers: { Accept: "application/json" } })
  }

  selectRender(renderId) {
    return fetch(this.selectRenderUrl, {
      method: "POST",
      headers: this.jsonHeaders(),
      body: JSON.stringify({ segment_index: this.segmentIndex, render_job_id: renderId }),
    })
  }

  startPreview() {
    return fetch(this.previewUrl, {
      method: "POST",
      headers: this.jsonHeaders(),
      body: JSON.stringify({ segment_index: this.segmentIndex }),
    })
  }

  previewStatus(renderJobId) {
    return fetch(`${this.previewStatusUrl}?render_job_id=${renderJobId}`)
  }

  // The media library index is a global endpoint (not segment-scoped); used by
  // the logo overlay "From library" browser.
  fetchMediaLibrary(type = "image") {
    return fetch(`/media.json?type=${type}`)
  }

  jsonHeaders() {
    return { "X-CSRF-Token": csrfToken(), "Content-Type": "application/json" }
  }
}
